import dedent from 'ts-dedent'
import { MILES_TO_M } from '../const'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Provider,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { SelectWorkflowComponent } from './common/selectcomponent'

class DistanceMatrix extends SelectWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.distancematrix',
        group: 'Spatial Operations',
        title: 'Distance Matrix',
        description: `
      This component creates a new table with a distance matrix computed for the geometries
      contained in an input table.
      `
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'geo',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column',
          parent: 'source',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'id',
          type: WorkflowComponentParamType.Column,
          title: 'Id column',
          parent: 'source',
          dataType: [ColumnDataType.String, ColumnDataType.Number]
        },
        {
          name: 'units',
          type: WorkflowComponentParamType.Selection,
          title: 'Units',
          options: ['Meters', 'Miles'],
          default: 'Meters'
        }
      ]
    )
  }

  getInnerSql({ params, connection }: WorkflowComponentCodeParams) {
    const convert = params.units === 'Meters' ? '' : ` / ${MILES_TO_M}`
    switch (connection.type) {
      case Provider.Redshift:
      case Provider.Postgres:
        return dedent`WITH dist AS(
                  SELECT
                      a.${params.id} AS id1,
                      b.${params.id} AS id2,
                      ST_DISTANCE(
                        a.${params.geo}::geography,
                        b.${params.geo}::geography
                      ) ${convert} AS distance
                  FROM
                    ${connection.quoteName(params.source as string)} AS a,
                    ${connection.quoteName(params.source as string)} AS b
                  WHERE a.${params.id} >= b.${params.id}
                )
                SELECT * FROM dist
                UNION ALL
                SELECT id2 as id1, id1 as id2, distance
                FROM dist`
      default:
        return dedent`WITH dist AS(
                  SELECT
                      a.${params.id} AS id1,
                      b.${params.id} AS id2,
                      ST_DISTANCE(
                        a.${params.geo},
                        b.${params.geo}
                      ) ${convert} AS distance
                  FROM
                    ${connection.quoteName(params.source as string)} AS a,
                    ${connection.quoteName(params.source as string)} AS b
                  WHERE a.${params.id} >= b.${params.id}
                )
                SELECT * FROM dist
                UNION ALL
                SELECT id2 as id1, id1 as id2, distance
                FROM dist`
    }
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns, params }: GetOutputSchemaParams): TableSchema {
    return [
      {
        name: 'id1',
        type: columns.source.find((c) => c.name === params.id)?.type as ColumnDataType
      },
      {
        name: 'id2',
        type: columns.source.find((c) => c.name === params.id)?.type as ColumnDataType
      },
      {
        name: 'distance',
        type: ColumnDataType.Number
      }
    ]
  }
  supportedProviders(): Provider[] {
    return [Provider.BigQuery, Provider.Snowflake, Provider.Redshift, Provider.Postgres]
  }
}

export { DistanceMatrix }
